import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { FeedbackContext } from '@cvt/contexts';


type PerformanceTabProps = {
  story: Stories.Story;
};

type CategoryPerformanceItemProps = {
  category: Analysis.CategoryPerformance;
};

const Stars: React.FC<{ value: number; outOf?: number }> = ({ value, outOf = 5 }) => {
  const stars = Array.from({ length: outOf }, (_, i) => i < value);
  return (
    <Box display="flex" alignItems="center">
      {stars.map((filled, idx) =>
        filled ? (
          <StarIcon key={idx} fontSize="small" sx={{ color: 'warning.main' }}/>
        ) : (
          <StarBorderIcon key={idx} fontSize="small" sx={{ color: 'warning.main' }}/>
        ),
      )}
    </Box>
  );
};

const CategoryPerformanceItem: React.FC<CategoryPerformanceItemProps> = ({ category }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const noteRef = useRef<HTMLSpanElement | null>(null);
  const note = category.note?.trim() || '';
  const hasNote = note.length > 0;

  const checkOverflow = useCallback(() => {
    const element = noteRef.current;
    if (!element) return;

    const isOverflowing =
      element.scrollHeight > element.clientHeight + 1 ||
      element.scrollWidth > element.clientWidth + 1;
    setShowToggle(isOverflowing);
  }, []);

  useEffect(() => {
    if (!hasNote) {
      setShowToggle(false);
      return;
    }

    if (!isExpanded) checkOverflow();
  }, [checkOverflow, hasNote, isExpanded, note]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 2,
        py: 1.25,
        bgcolor: 'background.paper',
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {category.name}
        </Typography>
        <Typography
          ref={noteRef}
          variant="caption"
          color="text.secondary"
          sx={
            isExpanded
              ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
              : {
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }
          }
        >
          {note}
        </Typography>
      </Box>

      <Box display="flex" alignItems="flex-start" gap={1} sx={{ flexShrink: 0, pt: 0.25 }}>
        {hasNote && showToggle && (
          <Tooltip title={isExpanded ? 'Hide full note' : 'Show full note'}>
            <IconButton
              size="small"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-label={isExpanded ? 'Hide full note' : 'Show full note'}
              sx={{ mt: 0.25 }}
            >
              {isExpanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
            </IconButton>
          </Tooltip>
        )}
        <Box display="flex" alignItems="center" gap={1}>
          <Stars value={category.stars}/>
          <Typography variant="caption" color="text.secondary">
            {category.stars}/5
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const PerformanceTab: React.FC<PerformanceTabProps> = ({ story }) => {
  const analysisData = story.structuralFeedback;
  const { triggerFeedback } = React.useContext(FeedbackContext);
  const ratingsContent = useMemo(() => {
    if (!analysisData) return '';
    const body = analysisData.categories
      .map(
        (category: Analysis.CategoryPerformance) =>
          `${category.name}: ${'★'.repeat(category.stars)}${'☆'.repeat(5 - category.stars)} (${category.stars}/5)\n  ${category.note || ''}`,
      )
      .join('\n\n');

    return `
Performance Rating Categories

${body}
    `
      .trim();
  }, [analysisData]);

  const copyRatings = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(ratingsContent);
      triggerFeedback({
        severity: 'success',
        message: 'Performance ratings copied to clipboard.' ,
      });
    } catch {
      triggerFeedback({
        severity: 'error',
        message: 'Unable to copy ratings.' ,
      });
    }
  }, [ratingsContent, triggerFeedback]);

  if (!analysisData) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
              No structural feedback available yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardHeader
            title={<Typography variant="subtitle1">Category Performance</Typography>}
            action={
              <Button variant="outlined" onClick={copyRatings} startIcon={<ContentCopyIcon fontSize="small"/>}>
                    Copy Ratings
              </Button>
            }
          />
          <CardContent>
            <Stack spacing={1.5}>
              {analysisData.categories.map((category: Analysis.CategoryPerformance, index: number) => (
                <CategoryPerformanceItem key={`${category.name}-${index}`} category={category}/>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};
